import { zodResolver } from "@hookform/resolvers/zod";
import { type DefaultValues, type FieldValues, useForm } from "react-hook-form";
import { useResetOnOpen } from "web_myplann/Shared/Hooks/useResetOnOpen";
import type { ZodType, ZodTypeDef } from "zod";

type ResettableFormSchema<
	TValues extends FieldValues,
	TModel extends FieldValues,
> = ZodType<TModel, ZodTypeDef & { typeName: string }, TValues>;

type Params<TValues extends FieldValues, TModel extends FieldValues> = {
	schema: ResettableFormSchema<TValues, TModel>;
	open: boolean;
	emptyValues: TValues;
	initialValues?: TValues;
};

export const useResettableForm = <
	TValues extends FieldValues,
	TModel extends FieldValues,
>({
	schema,
	open,
	emptyValues,
	initialValues,
}: Params<TValues, TModel>) => {
	const form = useForm<TValues, unknown, TModel>({
		resolver: zodResolver(schema),
		defaultValues: emptyValues as DefaultValues<TValues>,
		mode: "onChange",
	});

	useResetOnOpen(open, initialValues ?? emptyValues, form.reset);

	return form;
};
